// Offline queue for site surveys.
//
// When a survey can't reach the office (no signal), the page saves it here.
// As soon as the device is back online the queue uploads it through the normal
// save route, and removes the local copy once the office confirms the save.
// Nothing is removed without that confirmation.
import { z } from 'zod';
import {
  fetchServerSurveyVersion,
  PhotoUrlsNotSavedError,
  uploadQueuedSurvey,
  type QueuedSurveyUploadInput,
  type QueuedSurveyUploadResult,
  type ServerSurveyVersion,
} from '@/lib/site-survey-upload';
import { decideReplay, type ReplayContext, type ReplayDecision } from './offline-replay-policy';

// Storage keys - prefixed to avoid conflicts with auth/other storage
const STORAGE_KEYS = {
  SURVEYS: 'domelec_survey_offline_surveys',
  PENDING_UPLOADS: 'domelec_survey_pending_uploads',
  SYNC_STATUS: 'domelec_survey_sync_status',
  LAST_SYNC: 'domelec_survey_last_sync',
  OFFLINE_MODE: 'domelec_survey_offline_mode',
} as const;

const RETRY_CHECK_INTERVAL_MS = 60 * 1000;
const MAX_RETRY_DELAY_MS = 10 * 60 * 1000;

export const UPLOAD_MESSAGES = {
  waiting:
    'Changes to this survey are saved on this device. They will upload to the office automatically when you have signal.',
  photosWaiting:
    'The survey reached the office, but some photos are still on this device. They will keep uploading automatically.',
  photosRejected:
    "The survey reached the office, but some photos on this device couldn't be uploaded (they may be too large). They are kept here. Please contact the office.",
  needsReview:
    "This survey was changed in the office after you saved it on this device, so we haven't overwritten it. Your version is kept on this device. Check with the office, then upload your version if it's the right one.",
  rejected:
    'The office could not accept the changes saved on this device. They are kept here. Please contact the office.',
  unreadable:
    "Changes saved on this device couldn't be read. Please don't clear this browser's data, and contact the office.",
} as const;

// Older versions of the app wrote 'draft' | 'pending_sync' | 'synced' | 'sync_error'.
// None of those were ever confirmed by the office, so all of them count as waiting.
const offlineSurveySchema = z.object({
  localId: z.string(),
  surveyId: z.number().optional(),
  customerId: z.number(),
  installationId: z.number().optional(),
  data: z.any(), // Full survey form data
  status: z.enum(['waiting', 'needs_attention', 'draft', 'pending_sync', 'synced', 'sync_error']),
  createdAt: z.string(),
  updatedAt: z.string(),
  baseServerUpdatedAt: z.string().nullable().optional(),
  /** Staff chose "Upload my version anyway" after the office copy changed. */
  forceUpload: z.boolean().optional(),
  syncAttempts: z.number().default(0),
  lastAttemptAt: z.string().optional(),
  lastSyncError: z.string().optional(),
  photos: z
    .array(
      z.object({
        localId: z.string(),
        photoType: z.string(),
        dataUrl: z.string(),
        filename: z.string(),
        uploaded: z.boolean().default(false),
      })
    )
    .default([]),
});

export type OfflineSurvey = z.infer<typeof offlineSurveySchema>;

const syncStatusSchema = z.object({
  isOnline: z.boolean(),
  isSyncing: z.boolean(),
  pendingCount: z.number(),
  lastSyncTime: z.string().optional(),
});

type SyncStatus = z.infer<typeof syncStatusSchema>;

export type SurveyUploadState =
  | { state: 'none' }
  | { state: 'waiting'; message: string }
  | { state: 'needs_attention'; message: string; canUploadAnyway: boolean };

export interface SurveyUploader {
  fetchServerVersion(customerId: number): Promise<ServerSurveyVersion | null>;
  upload(input: QueuedSurveyUploadInput): Promise<QueuedSurveyUploadResult>;
}

const defaultUploader: SurveyUploader = {
  fetchServerVersion: fetchServerSurveyVersion,
  upload: uploadQueuedSurvey,
};

function isNeedsAttention(survey: OfflineSurvey): boolean {
  return survey.status === 'needs_attention';
}

/** HTTP status from an apiRequest error ("404: ...") or undefined for network errors. */
function httpStatusOf(error: unknown): number | undefined {
  const message = error instanceof Error ? error.message : String(error);
  const match = message.match(/^(\d{3}):/);
  return match ? Number(match[1]) : undefined;
}

/** Errors that should be retried later rather than shown to staff as a problem. */
function isTemporaryFailure(error: unknown): boolean {
  const status = httpStatusOf(error);
  if (status === undefined) return true; // offline, timeout, DNS, server asleep
  return status >= 500 || status === 401 || status === 403 || status === 408 || status === 429;
}

export class OfflineStorageService {
  private isSupported: boolean;
  private uploader: SurveyUploader;
  private retryInterval: ReturnType<typeof setInterval> | null = null;
  private startupTimer: ReturnType<typeof setTimeout> | null = null;
  private onlineStatusHandler: (() => void) | null = null;
  private offlineStatusHandler: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;
  private syncing: Promise<void> | null = null;
  private listeners = new Set<() => void>();
  // Customers whose survey is open in a tab that made the device copy itself.
  // That tab's form already holds the copy and saves it on reconnect, so the
  // queue leaves those alone to avoid two uploads racing each other.
  private claimed = new Set<number>();

  constructor(options: { uploader?: SurveyUploader } = {}) {
    this.uploader = options.uploader ?? defaultUploader;
    this.isSupported = this.checkStorageSupport();
    if (this.isSupported) {
      this.initializeEventListeners();
      this.startRetryTimer();
      // Upload anything left from an earlier visit as soon as the page is ready.
      this.startupTimer = setTimeout(() => this.syncInBackground(), 0);
    }
  }

  // Check if localStorage is supported and available
  private checkStorageSupport(): boolean {
    try {
      const testKey = '__domelec_storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      console.warn('LocalStorage not available:', e);
      return false;
    }
  }

  private initializeEventListeners(): void {
    this.onlineStatusHandler = () => {
      console.log('[OfflineStorage] Network online - uploading saved surveys');
      this.safeUpdateSyncStatus({ isOnline: true });
      this.syncInBackground();
    };

    this.offlineStatusHandler = () => {
      console.log('[OfflineStorage] Network offline');
      this.safeUpdateSyncStatus({ isOnline: false });
      this.notify();
    };

    // Phones often skip the 'online' event; re-check when the app comes back to the front.
    this.visibilityHandler = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        this.syncInBackground();
      }
    };

    window.addEventListener('online', this.onlineStatusHandler);
    window.addEventListener('offline', this.offlineStatusHandler);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  // Retry waiting surveys every minute. Cheap when nothing is waiting: it only reads storage.
  private startRetryTimer(): void {
    this.retryInterval = setInterval(() => this.syncInBackground(), RETRY_CHECK_INTERVAL_MS);
  }

  // Clean up event listeners and intervals
  public cleanup(): void {
    if (this.onlineStatusHandler) {
      window.removeEventListener('online', this.onlineStatusHandler);
    }
    if (this.offlineStatusHandler) {
      window.removeEventListener('offline', this.offlineStatusHandler);
    }
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    if (this.retryInterval) {
      clearInterval(this.retryInterval);
    }
    if (this.startupTimer) {
      clearTimeout(this.startupTimer);
    }
  }

  /** The open survey page will upload this customer's copy itself. */
  public claim(customerId: number): void {
    this.claimed.add(customerId);
  }

  /** Hand this customer's copy (if any) back to the background queue. */
  public release(customerId: number): void {
    if (this.claimed.delete(customerId)) this.syncInBackground();
  }

  /** Be told whenever the queue changes. Returns an unsubscribe function. */
  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) {
      try {
        listener();
      } catch (error) {
        console.warn('[OfflineStorage] Listener failed:', error);
      }
    }
  }

  /**
   * Save a survey that couldn't reach the office.
   *
   * `baseServerUpdatedAt` is the office copy's updatedAt that the form started
   * from (null if the office has no copy yet). It is recorded on the first
   * offline save only, so later conflict checks compare against where this
   * device's offline work began.
   */
  public saveSurvey(
    customerId: number,
    installationId: number | undefined,
    surveyId: number | undefined,
    data: any,
    baseServerUpdatedAt?: string | null
  ): string {
    if (!this.isSupported) {
      throw new Error('Offline storage not supported');
    }

    const surveys = this.getAllSurveys();
    // The office keeps one survey per customer, so this device does too.
    const existingIndex = surveys.findIndex(
      (s) => (surveyId !== undefined && s.surveyId === surveyId) || s.customerId === customerId
    );
    const existing = surveys[existingIndex];
    const localId =
      existing?.localId ?? (surveyId ? `survey_${surveyId}` : `customer_${customerId}`);

    // The form always sends its full state, so a photo missing from `data` was
    // removed by staff. Only photo lists written by older app versions are kept.
    const photos = [...(existing?.photos ?? [])];

    const now = new Date().toISOString();
    const offlineSurvey: OfflineSurvey = {
      ...existing,
      localId,
      surveyId: surveyId ?? existing?.surveyId,
      customerId,
      installationId: installationId ?? existing?.installationId,
      data,
      status: 'waiting',
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      baseServerUpdatedAt: existing ? existing.baseServerUpdatedAt : baseServerUpdatedAt,
      syncAttempts: 0,
      lastAttemptAt: undefined,
      lastSyncError: undefined,
      photos,
    };

    if (existingIndex >= 0) {
      surveys[existingIndex] = offlineSurvey;
    } else {
      surveys.push(offlineSurvey);
    }

    try {
      localStorage.setItem(STORAGE_KEYS.SURVEYS, JSON.stringify(surveys));
    } catch (e: any) {
      if (e.name === 'QuotaExceededError') {
        console.error('[OfflineStorage] Storage quota exceeded, attempting cleanup...');
        this.emergencyCleanup();
        try {
          localStorage.setItem(STORAGE_KEYS.SURVEYS, JSON.stringify(surveys));
        } catch (retryError) {
          console.error('[OfflineStorage] Still failed after cleanup:', retryError);
          throw new Error(
            'Browser storage is full. Existing offline surveys and photos have been kept. Free space on this device and try again.'
          );
        }
      } else {
        throw e;
      }
    }

    this.notify();
    return localId;
  }

  // Get all offline surveys
  public getAllSurveys(): OfflineSurvey[] {
    if (!this.isSupported) return [];

    try {
      const data = localStorage.getItem(STORAGE_KEYS.SURVEYS);
      if (!data) return [];

      const parsed = JSON.parse(data);
      if (!Array.isArray(parsed)) throw new Error('Invalid offline survey collection');
      return parsed;
    } catch (e) {
      console.error('Error reading offline surveys:', e);
      throw new Error(
        'Offline survey data could not be read. Existing browser data has been kept.'
      );
    }
  }

  // Get a specific survey by local ID
  public getSurvey(localId: string): OfflineSurvey | null {
    const surveys = this.getAllSurveys();
    return surveys.find((s) => s.localId === localId) || null;
  }

  /** Every survey still held on this device (none of them confirmed by the office). */
  public getPendingSurveys(): OfflineSurvey[] {
    return this.getAllSurveys();
  }

  /** What staff should be told about one customer's survey, if anything. */
  public getUploadState(customerId: number): SurveyUploadState {
    return this.getUploadStates().get(customerId) ?? { state: 'none' };
  }

  /** Upload state for every survey on this device, keyed by customer, from one storage read. */
  public getUploadStates(): Map<number, SurveyUploadState> {
    const states = new Map<number, SurveyUploadState>();
    for (const survey of this.getAllSurveys()) {
      if (isNeedsAttention(survey)) {
        states.set(survey.customerId, {
          state: 'needs_attention',
          message: survey.lastSyncError || UPLOAD_MESSAGES.rejected,
          canUploadAnyway: survey.lastSyncError === UPLOAD_MESSAGES.needsReview,
        });
      } else {
        const photosOnly = survey.lastSyncError === UPLOAD_MESSAGES.photosWaiting;
        states.set(survey.customerId, {
          state: 'waiting',
          message: photosOnly ? UPLOAD_MESSAGES.photosWaiting : UPLOAD_MESSAGES.waiting,
        });
      }
    }
    return states;
  }

  /**
   * The page saved this customer's survey to the office from a form that
   * already contained the offline changes (the copy is no newer than
   * `savedFrom`), so the copy on this device is no longer needed.
   *
   * If some photos still failed to upload, the copy is kept for them but
   * moved onto the office version this device just wrote, so the background
   * upload doesn't mistake this device's own save for someone else's change.
   */
  public markSavedToOffice(
    customerId: number,
    savedFrom: string,
    serverUpdatedAt: string | null,
    /** What the office now has, with failed photos still inline; null if all photos uploaded. */
    dataWithPendingPhotos: Record<string, any> | null,
    surveyId?: number
  ): void {
    const surveys = this.getAllSurveys();
    const survey = surveys.find((s) => s.customerId === customerId);
    if (!survey || survey.updatedAt > savedFrom) return;
    if (dataWithPendingPhotos) {
      // Replace the old offline snapshot so a later upload can't undo online edits.
      survey.data = dataWithPendingPhotos;
      survey.photos = [];
      survey.surveyId = surveyId ?? survey.surveyId;
      survey.baseServerUpdatedAt = serverUpdatedAt;
      survey.status = 'waiting';
      survey.forceUpload = false;
      survey.lastSyncError = UPLOAD_MESSAGES.photosWaiting;
      this.writeSurveys(surveys);
      return;
    }
    this.writeSurveys(surveys.filter((s) => s !== survey));
  }

  /**
   * Staff confirmed their device copy should replace the office copy that
   * changed while they were offline. Uploads straight away.
   */
  public uploadMyVersionAnyway(customerId: number): void {
    const surveys = this.getAllSurveys();
    const survey = surveys.find((s) => s.customerId === customerId);
    if (!survey || survey.lastSyncError !== UPLOAD_MESSAGES.needsReview) return;
    survey.status = 'waiting';
    survey.forceUpload = true;
    survey.syncAttempts = 0;
    survey.lastAttemptAt = undefined;
    survey.lastSyncError = undefined;
    this.writeSurveys(surveys);
    this.syncInBackground();
  }

  // Clear all offline data (but preserve auth)
  public clearAll(): void {
    if (
      this.getAllSurveys().length > 0 ||
      localStorage.getItem(STORAGE_KEYS.PENDING_UPLOADS) !== null
    ) {
      throw new Error('Offline surveys have no verified server acknowledgement and must be kept.');
    }
    // Only clear survey-specific keys, not auth
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
    this.notify();
  }

  public getSyncStatus(): SyncStatus {
    const pendingCount = this.getPendingSurveys().length;
    const fallback = { isOnline: navigator.onLine, isSyncing: false, pendingCount };
    if (!this.isSupported) return fallback;

    try {
      const data = localStorage.getItem(STORAGE_KEYS.SYNC_STATUS);
      if (!data) return fallback;
      const parsed = syncStatusSchema.passthrough().parse(JSON.parse(data));
      return {
        isOnline: navigator.onLine,
        isSyncing: this.syncing !== null,
        pendingCount,
        lastSyncTime: parsed.lastSyncTime,
      };
    } catch (e) {
      return fallback;
    }
  }

  private updateSyncStatus(updates: Partial<SyncStatus>): void {
    let current: Partial<SyncStatus> = {};
    try {
      current = JSON.parse(localStorage.getItem(STORAGE_KEYS.SYNC_STATUS) || '{}');
    } catch {
      current = {};
    }
    const updated = {
      isOnline: navigator.onLine,
      isSyncing: false,
      pendingCount: 0,
      ...current,
      ...updates,
    };
    // Older versions stored error banners here; they are no longer used.
    delete (updated as Record<string, unknown>).errors;
    localStorage.setItem(STORAGE_KEYS.SYNC_STATUS, JSON.stringify(updated));
  }

  private safeUpdateSyncStatus(updates: Partial<SyncStatus>): void {
    try {
      this.updateSyncStatus(updates);
    } catch (error) {
      console.warn(error instanceof Error ? error.message : 'Offline status could not be updated.');
    }
  }

  private writeSurveys(surveys: OfflineSurvey[]): void {
    localStorage.setItem(STORAGE_KEYS.SURVEYS, JSON.stringify(surveys));
    this.notify();
  }

  /** Re-read storage and change one survey, so edits made during an upload aren't lost. */
  private patchSurvey(
    localId: string,
    expectedUpdatedAt: string,
    patch: Partial<OfflineSurvey>
  ): void {
    const surveys = this.getAllSurveys();
    const index = surveys.findIndex((s) => s.localId === localId);
    if (index < 0) return;
    const current = surveys[index];
    if (current.updatedAt !== expectedUpdatedAt) {
      // Staff saved again while the upload ran. Keep their newer copy waiting,
      // but remember the office version it now builds on.
      if (patch.surveyId !== undefined) current.surveyId = patch.surveyId;
      if (patch.baseServerUpdatedAt !== undefined) {
        current.baseServerUpdatedAt = patch.baseServerUpdatedAt;
      }
      if (patch.forceUpload === false) current.forceUpload = false;
      surveys[index] = current;
    } else {
      surveys[index] = { ...current, ...patch };
    }
    this.writeSurveys(surveys);
  }

  /** Remove a survey only if it is still exactly the copy that the office confirmed. */
  private removeIfUnchanged(localId: string, expectedUpdatedAt: string, patch: Partial<OfflineSurvey>): void {
    const surveys = this.getAllSurveys();
    const current = surveys.find((s) => s.localId === localId);
    if (!current) return;
    if (current.updatedAt !== expectedUpdatedAt) {
      this.patchSurvey(localId, expectedUpdatedAt, patch);
      return;
    }
    this.writeSurveys(surveys.filter((s) => s.localId !== localId));
  }

  private isDueForRetry(survey: OfflineSurvey, now: number): boolean {
    if (isNeedsAttention(survey)) return false;
    if (!survey.lastAttemptAt || survey.syncAttempts === 0) return true;
    const delay = Math.min(
      RETRY_CHECK_INTERVAL_MS * 2 ** (survey.syncAttempts - 1),
      MAX_RETRY_DELAY_MS
    );
    return now - Date.parse(survey.lastAttemptAt) >= delay;
  }

  private syncInBackground(): void {
    void this.syncAllPending().catch((error) =>
      console.warn('[OfflineStorage] Upload check failed:', error)
    );
  }

  /** Upload every waiting survey. Safe to call often; only one run happens at a time. */
  public syncAllPending(options: { force?: boolean } = {}): Promise<void> {
    if (this.syncing) return this.syncing;
    this.syncing = this.runSync(options.force ?? false).finally(() => {
      this.syncing = null;
      this.notify();
    });
    return this.syncing;
  }

  private async runSync(force: boolean): Promise<void> {
    if (!navigator.onLine) return;

    // Unreadable storage is left exactly as it is; the hook reports it.
    const now = Date.now();
    const due = this.getAllSurveys().filter(
      (s) =>
        !this.claimed.has(s.customerId) &&
        (force ? !isNeedsAttention(s) : this.isDueForRetry(s, now))
    );
    if (due.length === 0) return;

    this.safeUpdateSyncStatus({ isSyncing: true });
    this.notify();
    let anyUploaded = false;
    try {
      for (const survey of due) {
        if (await this.uploadOne(survey)) anyUploaded = true;
      }
    } finally {
      this.safeUpdateSyncStatus({
        isSyncing: false,
        pendingCount: this.getPendingSurveys().length,
        ...(anyUploaded ? { lastSyncTime: new Date().toISOString() } : {}),
      });
    }
  }

  /** Returns true when the office confirmed this survey's fields were saved. */
  private async uploadOne(survey: OfflineSurvey): Promise<boolean> {
    const attemptAt = new Date().toISOString();
    try {
      const server = await this.uploader.fetchServerVersion(survey.customerId);
      const context: ReplayContext = {
        localSavedAt: survey.updatedAt,
        // Stays undefined for copies written by older app versions.
        baseServerUpdatedAt: survey.baseServerUpdatedAt,
        serverUpdatedAt: server?.updatedAt ?? null,
        serverStatus: server?.status ?? null,
      };
      const decision: ReplayDecision = survey.forceUpload ? 'upload' : decideReplay(context);

      if (decision === 'hold') {
        this.patchSurvey(survey.localId, survey.updatedAt, {
          status: 'needs_attention',
          lastAttemptAt: attemptAt,
          lastSyncError: UPLOAD_MESSAGES.needsReview,
        });
        return false;
      }

      if (decision === 'discard') {
        this.removeIfUnchanged(survey.localId, survey.updatedAt, {});
        return false;
      }

      const data = this.withLegacyPhotos(survey);
      // Never turn a survey the office has as completed back into a draft.
      if (server?.status === 'completed') data.status = 'completed';

      const result = await this.uploader.upload({
        customerId: survey.customerId,
        installationId: survey.installationId,
        surveyId: survey.surveyId ?? server?.id,
        data,
      });

      if (result.failedPhotoCount > 0) {
        // Fields are saved; the photos that failed are still inline in result.data.
        const onlyPermanent = result.permanentPhotoFailureCount === result.failedPhotoCount;
        this.patchSurvey(survey.localId, survey.updatedAt, {
          status: onlyPermanent ? 'needs_attention' : 'waiting',
          surveyId: result.surveyId,
          data: result.data,
          photos: [],
          baseServerUpdatedAt: result.serverUpdatedAt,
          forceUpload: false,
          syncAttempts: survey.syncAttempts + 1,
          lastAttemptAt: attemptAt,
          lastSyncError: onlyPermanent
            ? UPLOAD_MESSAGES.photosRejected
            : UPLOAD_MESSAGES.photosWaiting,
        });
      } else {
        this.removeIfUnchanged(survey.localId, survey.updatedAt, {
          surveyId: result.surveyId,
          baseServerUpdatedAt: result.serverUpdatedAt,
          forceUpload: false,
        });
      }
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.warn(`[OfflineStorage] Upload of ${survey.localId} failed:`, message);
      // Photos already reached the office: keep their URLs so a retry doesn't
      // upload them again.
      const photosUploaded: Partial<OfflineSurvey> =
        error instanceof PhotoUrlsNotSavedError
          ? { data: error.data, photos: [], surveyId: error.surveyId }
          : {};
      if (isTemporaryFailure(error)) {
        this.patchSurvey(survey.localId, survey.updatedAt, {
          ...photosUploaded,
          status: 'waiting',
          syncAttempts: survey.syncAttempts + 1,
          lastAttemptAt: attemptAt,
        });
      } else {
        this.patchSurvey(survey.localId, survey.updatedAt, {
          ...photosUploaded,
          status: 'needs_attention',
          lastAttemptAt: attemptAt,
          lastSyncError: UPLOAD_MESSAGES.rejected,
        });
      }
      return false;
    }
  }

  /**
   * Older app versions moved some offline photos out of the survey data into a
   * separate list. Put each one back into its field when that field is still
   * empty so it uploads; a field that now has a value already has a newer photo.
   */
  private withLegacyPhotos(survey: OfflineSurvey): Record<string, any> {
    const data = JSON.parse(JSON.stringify(survey.data ?? {}));
    for (const photo of survey.photos ?? []) {
      if (photo.uploaded) continue;
      const parts = photo.photoType.split('.');
      let target: any = data;
      for (let index = 0; index < parts.length - 1; index += 1) {
        const part = parts[index];
        if (target[part] === undefined || target[part] === null) {
          target[part] = /^\d+$/.test(parts[index + 1]) ? [] : {};
        }
        target = target[part];
      }
      const last = parts[parts.length - 1];
      if (target[last] === undefined || target[last] === null || target[last] === '') {
        target[last] = photo.dataUrl;
      }
    }
    return data;
  }

  // Get storage usage info
  public getStorageInfo(): {
    used: number;
    surveyCount: number;
    photoCount: number;
    oldestSurvey: string | null;
  } {
    const surveys = this.getAllSurveys();
    const storageUsed = new Blob([JSON.stringify(surveys)]).size;
    const photoCount = surveys.reduce((acc, s) => acc + (s.photos?.length ?? 0), 0);

    const oldest = surveys.reduce(
      (oldest, survey) => {
        if (!oldest || new Date(survey.createdAt) < new Date(oldest)) {
          return survey.createdAt;
        }
        return oldest;
      },
      null as string | null
    );

    return {
      used: storageUsed,
      surveyCount: surveys.length,
      photoCount,
      oldestSurvey: oldest,
    };
  }

  public cleanupOldSurveys(): number {
    return 0;
  }

  public manageStorageQuota(): void {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      void navigator.storage
        .estimate()
        .then(({ usage, quota }) => {
          if (usage && quota && usage / quota > 0.8) {
            console.warn(
              '[OfflineStorage] Storage is nearly full; offline surveys and photos are being retained.'
            );
          }
        })
        .catch((error) => console.warn('[OfflineStorage] Storage estimate failed:', error));
    }
  }

  public emergencyCleanup(): void {
    console.warn(
      '[OfflineStorage] Offline surveys and photos have no verified server acknowledgement; retaining them.'
    );
  }

  public clearAllPhotoData(): void {
    console.warn('[OfflineStorage] Retaining photos without verified server acknowledgement.');
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorageService();
